import React from 'react';
import { motion } from 'framer-motion';
import { User, Search, BarChart2, Shield, CheckCircle, Check } from 'lucide-react';

const MDRServices = () => {
  // Adjusted offsets to create the "in-and-out" curve seen in the photo
  const steps = [
    { id: 1, title: "Compromise", icon: <User />, color: "#FFD700", offset: 30 },
    { id: 2, title: "Detection", icon: <Search />, color: "#FF8C00", offset: 110 },
    { id: 3, title: "Analysis", icon: <BarChart2 />, color: "#FF1493", offset: 150 },
    { id: 4, title: "Response", icon: <Shield />, color: "#9370DB", offset: 110 },
    { id: 5, title: "Remediation", icon: <CheckCircle />, color: "#00BFFF", offset: 30, isFinal: true },
  ];

  return (
    <section className="overflow-hidden font-sans">
      <div className="max-w-7xl mx-auto px-1 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"> 
  
        {/* Left Content */}
        <div className="space-y-3">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#4CBB17] leading-tight">
            24/7 Threat Neutralization, <br />
            <span className="text-[#FFD700]">Not Just Alerts</span>
          </h2>
          <p 
            className="text-white-600 text-lg leading-snug max-w-none"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
          >
            SecuWall’s Managed Detection and Response (MDR) combines elite human experts with advanced AI to actively monitor, 
            detect, and <span className="text-[#FFD700]">stop threats 24/7—before they turn into costly breaches. </span>  
            Our MDR service gives you an always-on security team that doesn’t just send alerts—we take action. 
            We monitor entire environment (endpoints, cloud, network) around the clock, spot real threats quickly, and neutralize them fast—so damage never happens.
          </p>
        </div>

        {/* Right Diagram Container */}
        <div className="relative w-full h-[380px] sm:h-[550px] lg:h-[650px] flex items-center justify-start overflow-visible mt-[-60px] lg:mt-0 pl-6 sm:pl-10 lg:pl-0">
          
          {/* Change origin-center to origin-left to push the scaled diagram to the left */}
          <div className="relative w-full h-full scale-[0.45] xs:scale-[0.60] sm:scale-[0.80] lg:scale-100 origin-left transition-transform duration-500">
            
            <div className="relative w-full h-full">
                {/* Central MDR Hub - Using your exact Top logic */}
                <div className="absolute left-0 top-[325px] -translate-y-1/2 z-20 w-64 h-48 bg-[#f1f4f9] border border-blue-200 rounded-3xl flex items-center justify-center p-8 shadow-sm">
                  <h3 className="text-2xl font-black text-[#1a2b56] text-center leading-tight">
                    Managed Detection <br /> and Response
                  </h3>
                </div>

                {/* Connection Lines (SVG) */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible z-10">
                  {steps.map((step, i) => {
                    const cardSize = 90;
                    const hOffsets = [50, 150, 230, 150, 50];
                    const vPositions = [150, 190, 280, 370, 410];
                    const startX = 256; 
                    const hubCenterY = 325; 
                    const startY = (hubCenterY - 24) + (i * 12); 
                    const cardLeft = 280 + hOffsets[i];
                    const cardTop = vPositions[i];
                    const cardCenterX = cardLeft + (cardSize / 2);
                    const cardCenterY = cardTop + (cardSize / 2);

                    let endX, endY, d;
                    const r = 35;

                    if (i < 2) {
                      endX = cardCenterX; endY = cardTop + cardSize;
                      d = `M ${startX} ${startY} L ${endX - r} ${startY} Q ${endX} ${startY} ${endX} ${startY - r} L ${endX} ${endY}`;
                    } else if (i === 2) {
                      endX = cardLeft; endY = cardCenterY;
                      d = `M ${startX} ${startY} L ${endX} ${endY}`;
                    } else {
                      endX = cardCenterX; endY = cardTop;
                      d = `M ${startX} ${startY} L ${endX - r} ${startY} Q ${endX} ${startY} ${endX} ${startY + r} L ${endX} ${endY}`;
                    }

                    return (
                      <g key={`path-group-${i}`}>
                        <circle cx={startX} cy={startY} r="3" fill={step.color} />
                        <motion.path
                          d={d}
                          stroke={step.color}
                          strokeWidth="2.5"
                          fill="none"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          initial={{ pathLength: 0, opacity: 0 }}
                          whileInView={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.2, ease: "easeInOut", delay: i * 0.1 }}
                        />
                        <circle cx={endX} cy={endY} r="3" fill={step.color} />
                      </g>
                    );
                  })}
                </svg>

                {/* Cascading Node Cards */}
                <div className="absolute left-0 w-full h-full pointer-events-none z-20">
                  {steps.map((step, i) => {
                    const cardSize = 90;
                    const hOffsets = [50, 150, 240, 150, 50];
                    const vPositions = [140, 180, 280, 380, 420];
                    const leftPos = 280 + hOffsets[i];
                    const topPos = vPositions[i];

                    return (
                      <motion.div
                        key={step.id}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        className="absolute flex items-center pointer-events-auto"
                        style={{ top: `${topPos}px`, left: `${leftPos}px` }}
                      >
                        <div 
                          className="relative flex-shrink-0 flex items-center justify-center bg-white rounded-xl shadow-lg border-l-4"
                          style={{ width: `${cardSize}px`, height: `${cardSize}px`, borderLeftColor: step.color }}
                        >
                          <div className="text-gray-700">
                            {React.cloneElement(step.icon, { size: 32, strokeWidth: 1.5 })}
                          </div>
                        </div>

                        <div className={`absolute left-0 w-full text-center whitespace-nowrap ${i < 3 ? "-top-8" : "top-[100%] mt-2"}`}>
                          <span className="text-sm font-bold text-white">
                            {step.title}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MDRServices;